import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { authApi } from "@/lib/api";
import { useLocation } from "wouter";
import { trackLogout } from "@/lib/analytics-advanced";

interface User {
  id: string;
  username: string;
  email: string;
  role: "superadmin" | "admin" | "creator" | "client" | "student";
  name: string;
  avatarUrl?: string | null;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setLocation] = useLocation();

  const checkAuth = async () => {
    try {
      const response = await authApi.me();
      setUser(response.user);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (username: string, password: string) => {
    const response = await authApi.login(username, password);
    setUser(response.user);
    
    // Redirect based on role
    if (response.user.role === "admin" || response.user.role === "superadmin") {
      setLocation("/admin");
    } else if (response.user.role === "client") {
      setLocation("/marca");
    } else if (response.user.role === "student") {
      setLocation("/student/dashboard");
    } else {
      setLocation("/creator");
    }
  };

  const logout = async () => {
    trackLogout();
    await authApi.logout();
    setUser(null);
    setLocation("/admin/login");
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
